import { ChevronDown } from "lucide-react";
import { useId, type ReactNode } from "react";

/**
 * One email in a list: its name, who it goes to and whether it has gone
 * out, with the options and send steps underneath once opened.
 */
export function EmailRow({
  level,
  title,
  audienceLine,
  statusLine,
  open,
  onToggle,
  children,
}: {
  /** Heading level for the title, under the section around it. */
  level: 3 | 4;
  title: string;
  /** "Goes to the 14 people who booked". */
  audienceLine: string;
  /** "Last sent 12 Sep, 7:40 pm by Shekher, to 14 people" or "Not sent
   *  yet"; null while the history is loading. */
  statusLine: string | null;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const panelId = useId();
  const Heading = level === 3 ? "h3" : "h4";
  return (
    <li className="surface">
      <Heading>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          className="flex min-h-11 w-full items-start justify-between gap-4 rounded-lg px-4 py-4 text-left sm:px-5"
        >
          <span className="min-w-0">
            <span className="block text-lead font-semibold text-[color:var(--color-ink)]">
              {title}
            </span>
            <span className="mt-1 block text-[15px] font-normal text-[color:var(--color-ink-soft)]">
              {audienceLine}
            </span>
            {statusLine ? (
              <span className="mt-1 block text-[14px] font-normal text-[color:var(--color-ink-faint)]">
                {statusLine}
              </span>
            ) : null}
          </span>
          <ChevronDown
            size={20}
            aria-hidden
            className={`mt-1 shrink-0 text-[color:var(--color-ink-soft)] transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </Heading>
      {open ? (
        <div id={panelId} className="px-4 pb-5 sm:px-5">
          {children}
        </div>
      ) : null}
    </li>
  );
}
